import { NextStep } from '@core/services/up-next.service';
import { StepUtils } from '@shared/utils/step.utils';
import { createSelector } from '@ngrx/store';
import {
  doAnyProductsHaveErrorsOrBlocked,
  getProductsPending,
  getSelectedProductsIncludingNWXInactive,
} from '../entities/product/product.selectors';
import {
  allDraftTasksComplete,
  allQuotedTasksComplete,
} from '../entities/task/selectors/task.selector';
import {
  getShowTelematicsConfirmationTask,
  getTelematicsLoading,
  getTelematicsReviewed,
} from '../entities/telematics/telematics.selector';
import { getPreBindTaskStatus } from '../entities/confirmations/confirmations.selector';
import { getQuoteOrUpdateInProgressCount } from '../entities/quote/quote.selector';
import { PreBindDocumentSelectors } from '.';

const getNextStepProductsThings = createSelector(
  getSelectedProductsIncludingNWXInactive,
  getProductsPending,
  doAnyProductsHaveErrorsOrBlocked,
  getQuoteOrUpdateInProgressCount,
  getTelematicsLoading,
  (
    products,
    productsPending,
    anyProductsHaveErrorsOrBlocked,
    quoteInProgressCount,
    telematicsLoading
  ) => ({
    products,
    productsPending,
    anyProductsHaveErrorsOrBlocked,
    quoteInProgressCount,
    telematicsLoading,
  })
);

export const getNextStep = createSelector(
  getNextStepProductsThings,
  allDraftTasksComplete,
  allQuotedTasksComplete,
  getShowTelematicsConfirmationTask,
  getTelematicsReviewed,
  getPreBindTaskStatus,
  PreBindDocumentSelectors.getPreBindCallsIdle,
  (
    {
      products,
      productsPending,
      anyProductsHaveErrorsOrBlocked,
      quoteInProgressCount,
      telematicsLoading,
    },
    draftTasksComplete,
    quotedTasksComplete,
    showTelematics,
    telematicsReviewed,
    preBindTaskStatus,
    preBindCallsIdle
  ): NextStep => {
    // This is temporary, this now needs to be a service with CombineLatest
    const activeProducts = products.filter((product) => product.isDsmActive);
    const inactiveProducts = products.filter((product) => !product.isDsmActive);

    const allProductsBinding =
      activeProducts.length > 0 &&
      activeProducts.every((p) => p.quoteStatus === 'Binding');

    const allProductsQuoted =
      activeProducts.length > 0 &&
      activeProducts.every(
        (p) => p.quoteStatus === 'Quoted' || p.quoteStatus === 'Binding'
      );

    const quoteHasPersonalAuto =
      activeProducts.length > 0 &&
      activeProducts.some((p) => p.type === 'PersonalAuto');

    if (productsPending || quoteInProgressCount > 0 || telematicsLoading) {
      return StepUtils.getProductsPending();
    }
    
    if (anyProductsHaveErrorsOrBlocked) {
      // return StepUtils.getRemoveProductsWithErrors();
      return StepUtils.completeQuotedTasks();
    }

    if (!draftTasksComplete) {
      return StepUtils.completeDraftTasks();
    }

    if (!quotedTasksComplete && allProductsQuoted) {
      return StepUtils.completeQuotedTasks();
    }

    if (quoteHasPersonalAuto && !telematicsReviewed && showTelematics && allProductsQuoted) {
      // TODO - Might be an issue here, could need to launch the telematics modal or something
      return StepUtils.reviewTelematics();
    }

    if (quoteHasPersonalAuto && showTelematics && allProductsQuoted) {
      return StepUtils.acceptTelematics();
    }

    if (draftTasksComplete && quotedTasksComplete && allProductsQuoted) {
      switch (preBindTaskStatus) {
        case 'not needed':
          break;
        case 'ready':
          break;
        case 'pending':
          return StepUtils.acceptPreBindDocuments(preBindCallsIdle);
        case 'confirm':
          return StepUtils.acceptPreBindDocuments(preBindCallsIdle);
        case 'upload':
          return StepUtils.uploadPreBindDocuments(preBindCallsIdle);
      }
    }

    // if all products are quoted and previous steps complete offer bind
    if (
      draftTasksComplete &&
      quotedTasksComplete &&
      allProductsQuoted &&
      !allProductsBinding
    ) {
      return StepUtils.getFinalRate();
    }

    // if all products are final and telematics confirmed, offer to go Billing
    if (activeProducts.length > 0 && allProductsBinding) {
      return StepUtils.getGoToPayBind();
    }

    if (activeProducts.length === 0 && inactiveProducts.length > 0) {
      return StepUtils.pivotToPolicyCenter();
    }

    if (products.length === 0 && inactiveProducts.length === 0) {
      return StepUtils.noProducts();
    }

    return StepUtils.getRecalculateRate();
  }
);
